import React, { FC, useEffect, useRef, useState } from "react";

import classnames from "classnames";
import "./NumberInput.scss";

interface Props {
  numberValue: string[];
  setNumberValue: (numberValue: string[]) => void;
  isPhoneNumber?: boolean;
  onSubmit?: (e: React.KeyboardEvent<HTMLInputElement>) => {};
}

const NumberInput: FC<Props> = ({ numberValue, setNumberValue, isPhoneNumber, onSubmit }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState(0);
  const mockOnChange = () => null;

  const changeSplitNumber = (value: string, index: number) => {
    const newNumberValue = [...numberValue];
    newNumberValue[index] = value;

    setNumberValue(newNumberValue);
  };

  useEffect(() => {
    focusCurrentInput();
  }, [position]);

  const focusCurrentInput = () => {
    if (ref.current === null) {
      return;
    }
    ref.current.focus();
  };

  const isNumber = (str: string) => {
    const numberRegx = /^[0-9]+$/g;
    return numberRegx.test(str);
  };

  const handleBackSpace = (index: number) => {
    if (numberValue[index] === "") {
      const newIndex = Math.max(0, index - 1);
      changeSplitNumber("", newIndex);
      setPosition(newIndex);
    } else {
      changeSplitNumber("", index);
    }
  };

  const changeChar = index => (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;

    if (key === "Backspace") {
      handleBackSpace(index);
      return;
    }

    if (key === "Enter") {
      e.stopPropagation();
      onSubmit(e);
      return;
    }

    if (!isNumber(key)) {
      return;
    }

    // assuming key is acsii value
    changeSplitNumber(key, position);
    setPosition(Math.min(position + 1, numberValue.length - 1));
  };

  const handlePaste = index => (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText: string = e.clipboardData.getData("Text");
    const targetNumberValue = pastedText
      .split("")
      .filter(isNumber)
      .slice(0, numberValue.length - index);

    const newNumberValue = [
      ...numberValue.slice(0, index),
      ...targetNumberValue,
      ...numberValue.slice(index + targetNumberValue.length),
    ];

    setNumberValue(newNumberValue);
    setPosition(Math.min(index + targetNumberValue.length, numberValue.length - 1));
  };

  const changePos = (index: number) => () => {
    if (index < numberValue.length) {
      setPosition(index);
    }
  };

  const bindEle = (index: number) => el => {
    if (position === index) {
      ref.current = el;
    }
  };

  const buildInputClassNames = () => {
    return classnames("number-value", {
      "is-phone-number": isPhoneNumber,
    });
  };

  const renderInput = (value: string, index: number) => {
    return (
      <input
        key={index}
        className={buildInputClassNames()}
        ref={bindEle(index)}
        value={value}
        onClick={changePos(index)}
        onKeyDown={changeChar(index)}
        onPaste={handlePaste(index)}
        onChange={mockOnChange}
        onSubmit={onSubmit}
      />
    );
  };

  const renderInputs = () => {
    if (isPhoneNumber) {
      return renderPhoneNumber();
    } else {
      return renderCode();
    }
  };

  const renderPhoneNumber = () => {
    const renderedNumber = numberValue.map(renderInput);
    renderedNumber.splice(
      1,
      0,
      <span key="phone-number-1" className="phone-number-bracket">
        (
      </span>
    );
    renderedNumber.splice(
      5,
      0,
      <span key="phone-number-2" className="phone-number-bracket">
        )
      </span>
    );
    renderedNumber.splice(9, 0, <span key="spacer-1" className="number-spacer" />);
    return renderedNumber;
  };

  const renderCode = () => {
    const renderedNumber = numberValue.map(renderInput);
    renderedNumber.splice(3, 0, <span key="spacer-2" className="number-spacer" />);
    return renderedNumber;
  };

  return <span>{renderInputs()}</span>;
};

export { NumberInput };
